/* eslint-disable no-nested-ternary */
import * as React from 'react';
import Box from '@mui/material/Box';
import { Stack, useMediaQuery } from '@mui/material';
import { AppointmentType } from '@prisma/client';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import TabBar from '../components/filtering/TabBar';
import {
  Appointment,
  FooterAction,
  GeneralRequest,
  LoadedUser,
  ModalData,
} from '../interfaces';
import ReuseableForm from '../components/form/ReuseableForm';
import SideBarModal from '../components/modal/SideBarModal';
import FullPageModal from '../components/modal/FullPageModal';
import {
  rejectFormValues,
  rejectValidationSchema,
  rejectInputGroups,
  RejectValues,
} from '../utils/form/rejectForm';
import { createModalData } from '../utils/modal/requestModal';
import {
  archiveAppointment,
  changeRequestArtist,
  createAppointmentChat,
  forwardGeneralRequestToArtist,
  invalidateAppointments,
  invalidateRequests,
  invalidateUnreadRequests,
  rejectAppointment,
  rejectGeneralRequest,
  sendAppointmentReminder,
  unarchiveAppointment,
  uncancelAppointment,
  undoRejection,
  updateOfferedSlots,
} from '../services/appointments';
import Error from '../components/global/Error';
import ConfirmationModal from '../components/modal/ConfirmationModal';
import AppointmentForm from '../components/form/AppointmentForm';
import EditRequestForm from '../components/form/EditRequestForm';
import useUser from '../hooks/global/useUser';
import useSizeOptions from '../hooks/tattoos/useSizeOptions';
import useGetRequests from '../hooks/requests/useGetRequests';
import AcceptRequestFlow from '../components/form/AcceptRequestFlow';
import EditOfferedTimesDialog from '../components/appointments/EditOfferedTimesDialog';
import { splitOfferedSlots } from '../utils/form/offeredSlots';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import {
  UnarchivedAppointmentFields,
  UnarchivedGeneralRequestFields,
} from '../types/appointments';
import { Feature } from '../types/subscriptions';
import useSubscriptionModal from '../hooks/subscriptions/useSubscriptionModal';
import {
  isAppointmentDepositPaid,
  navigateToChat,
} from '../helpers/appointments';
import PageLayout from '../components/global/PageLayout';
import { createFooterAction } from '../helpers/global';
import useSort from '../hooks/global/useSort';
import { SORTABLE_KEYS } from '../constants/appointments';
import useGetUnreadRequests from '../hooks/requests/useGetUnreadRequests';
import useMarkRequestAsRead from '../hooks/requests/useMarkRequestAsRead';
import useNavigateToCustomerProfile from '../hooks/customers/useNavigateToCustomerProfile';
import { ROUTES } from '../constants/global';
import useRequestsFilters from '../hooks/requests/useRequestsFilters';
import MultiSelectFilter from '../components/filtering/MultiSelectFilter';
import useGetAppointment from '../hooks/appointments/useGetAppointment';
import useLoadingModal from '../hooks/global/useLoadingModal';
import useAwaitedForm from '../hooks/useAwaitedForm';
import { getEmployeeCalendarName } from '../helpers/calendar';
import useConfirm from '../hooks/useConfirm';
import { isUserAdminOrManager } from '../utils/global/helper';
import { InputRow } from '../components/global/FormSmallModal';
import useDepositCheckoutForm from '../hooks/global/useDepositCheckoutForm';
import RequestsTable from '../components/appointments/RequestsTable';
import CoachMark from '../components/global/CoachMark';
import { dismissUserHint } from '../helpers/userHints';
import RequestsList from '../components/appointments/RequestsList';
import useDeliveryMethods from '../hooks/global/useDeliveryMethods';

export type Tab =
  | 'REQUESTED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'ARCHIVED'
  | 'CANCELED';
type Appt = Appointment | GeneralRequest;

// Offered times only exist on accepted requests; both menus gate on this
function getApptWithOfferedTimes(appointment: Appt | undefined) {
  return !!appointment &&
    'status' in appointment &&
    appointment.status === 'ACCEPTED' &&
    appointment.offeredSlots?.length
    ? appointment
    : null;
}

const TAB_ORDER = [
  'REQUESTED',
  'ACCEPTED',
  'REJECTED',
  'CANCELED',
  'ARCHIVED',
] as const satisfies readonly Tab[];

function getTabValue(tab: Tab) {
  const index = TAB_ORDER.indexOf(tab as (typeof TAB_ORDER)[number]);
  return index === -1 ? 0 : index;
}

const CHANGE_ARTIST_STATUS = ['ACCEPTED', 'REQUESTED'];
const EDIT_REQUEST_STATUS = ['ACCEPTED', 'REQUESTED'];
// Tab name constants
const TAB_NAMES = {
  NEW_REQUESTS: 'New Requests',
  ACCEPTED_REQUESTS: 'Accepted Requests',
  REJECTED_REQUESTS: 'Rejected Requests',
  CANCELED_BY_CUSTOMER: 'Canceled by Customer',
  CALENDAR: 'Calendar',
} as const;

export default function RequestsPage() {
  const { appointmentId } = useParams();
  const isLgScreen = useMediaQuery('(min-width:900px)');
  const navigate = useNavigate();
  const navigateToCustomerProfile = useNavigateToCustomerProfile();
  const asyncForm = useAwaitedForm();
  const confirm = useConfirm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { openDepositCheckout } = useDepositCheckoutForm();

  const [currentTab, setCurrentTab] = React.useState<Tab>('REQUESTED');

  const [formError, setFormError] = React.useState('');

  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalData, setModalData] = React.useState<ModalData>();
  const [acceptModalOpen, setAcceptModalOpen] = React.useState(false);
  const [offeredTimesAppointment, setOfferedTimesAppointment] =
    React.useState<Appointment | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = React.useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = React.useState(false);
  const [editRequestModalOpen, setEditRequestModalOpen] = React.useState(false);
  const [appointmentFormModalOpen, setAppointmentFormModalOpen] =
    React.useState(false);
  const [shouldScrollToTop, setShouldScrollToTop] = React.useState(false);
  const acceptDirtyCheckRef = React.useRef<(() => boolean) | null>(null);

  const [selectedAppointment, setSelectedAppointment] = React.useState<Appt>();

  const { artist } = useRequestsFilters();
  const { user: loadedUser } = useUser<LoadedUser>();

  const { sizeOptions } = useSizeOptions();
  const sort = useSort(SORTABLE_KEYS, {
    defaultSort: {
      orderBy: 'createdAt',
      order: 'desc',
    },
  });

  const { data: unreadData } = useGetUnreadRequests();
  const {
    appointments: unreadAppointments,
    generalRequests: unreadGeneralRequests,
  } = unreadData ?? {};
  const { mutate: markRequestAsRead } = useMarkRequestAsRead();

  const [createAptType, setCreateAptType] =
    React.useState<AppointmentType>('NEW_APPOINTMENT');

  const onCloseRequest = React.useCallback(() => {
    setModalOpen(false);
    setModalData(undefined);
    if (appointmentId) {
      navigate(ROUTES.requests, {
        replace: true,
        preventScrollReset: true,
      });
    }
  }, [appointmentId, navigate]);

  const {
    combinedAppointments: appointments,
    isLoading: loading,
    isFetching,
    isError,
    totalCount,
    isPlaceholderData,
    hasNextPage,
    fetchNextPage,
  } = useGetRequests({
    status: currentTab,
    enabled: !!currentTab,
    sort,
    employeeIds: artist.state,
    isCanceledByCustomer: currentTab === 'CANCELED',
  });
  const isLoading = loading || isPlaceholderData;

  const { openSubscriptionModal } = useSubscriptionModal();
  const canManageRequest = useFeatureGuard(Feature.MANAGE_REQUESTS);
  const getDeliveryMethods = useDeliveryMethods();

  const closeAllModals = React.useCallback(() => {
    onCloseRequest();
    setAcceptModalOpen(false);
    setRejectModalOpen(false);
    setArchiveModalOpen(false);
    setEditRequestModalOpen(false);
    setAppointmentFormModalOpen(false);
  }, [onCloseRequest]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onEndReached = React.useCallback(async () => {
    if (!hasNextPage || isFetching) return;
    await fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetching]);

  // Set up click action for each row of the requests table
  const openRequest = React.useCallback(
    (newModalData: ModalData, appointment: Appt) => {
      if (!canManageRequest) {
        openSubscriptionModal();
        return;
      }
      if (
        'status' in appointment &&
        unreadAppointments?.includes(appointment.appointmentId)
      ) {
        markRequestAsRead({
          requestId: appointment.appointmentId,
          type: 'appointment',
        });
      } else if (
        'requestId' in appointment &&
        unreadGeneralRequests?.includes(appointment.requestId)
      ) {
        markRequestAsRead({
          requestId: appointment.requestId,
          type: 'generalRequest',
        });
      }
      setSelectedAppointment(appointment);
      setModalData(newModalData);
      setModalOpen(true);
    },
    [
      canManageRequest,
      markRequestAsRead,
      openSubscriptionModal,
      unreadAppointments,
      unreadGeneralRequests,
    ],
  );

  // Set up click actions for modal footer
  const acceptRequest = React.useCallback(() => {
    if (!canManageRequest) {
      openSubscriptionModal();
      return;
    }
    // Opening the flow is the lesson — retire the coach mark
    dismissUserHint('ACCEPT_REQUEST_FLOW');
    setAcceptModalOpen(true);
  }, [canManageRequest, openSubscriptionModal]);

  // Set up click actions for modal footer
  const openArchive = React.useCallback(() => {
    if (!canManageRequest) {
      openSubscriptionModal();
      return;
    }
    setArchiveModalOpen(true);
  }, [canManageRequest, openSubscriptionModal]);

  const rejectRequest = React.useCallback(() => {
    if (!canManageRequest) {
      openSubscriptionModal();
      return;
    }
    setRejectModalOpen(true);
  }, [canManageRequest, openSubscriptionModal]);

  const openEditRequest = React.useCallback(() => {
    if (!canManageRequest) {
      openSubscriptionModal();
      return;
    }
    setEditRequestModalOpen(true);
  }, [canManageRequest, openSubscriptionModal]);

  const closeAppointmentForm = React.useCallback(() => {
    setAppointmentFormModalOpen(false);
  }, []);

  const openAppointmentForm = React.useCallback(
    (type: AppointmentType) => {
      if (!canManageRequest) {
        openSubscriptionModal();
        return;
      }
      setCreateAptType(type);
      setAppointmentFormModalOpen(true);
    },
    [canManageRequest, openSubscriptionModal],
  );

  const smartRefresh = React.useCallback(
    async (
      appointment: Appointment | GeneralRequest,
      action?: 'accept' | 'reject' | 'archive' | 'offered-times',
    ) => {
      if (!appointment) return;
      if (!('status' in appointment) || appointment.status === 'REQUESTED') {
        invalidateRequests('REQUESTED');
      } else if (appointment.status === 'ACCEPTED') {
        invalidateRequests('ACCEPTED');
        // A declined offer is listed under new requests, so refresh both
        if (appointment.offeredTimesDeclinedAt) {
          invalidateRequests('REQUESTED');
        }
      } else if (appointment.status === 'REJECTED') {
        invalidateRequests('REJECTED');
      } else if (appointment.status === 'ARCHIVED') {
        invalidateRequests('ARCHIVED');
      } else if (appointment.status === 'CANCELED') {
        invalidateRequests('CANCELED');
      }

      // invalidate the accepted appointments query if the appointment was accepted
      if (action === 'accept') {
        invalidateRequests('ACCEPTED');
      } else if (action === 'reject') {
        invalidateRequests('REJECTED');
      } else if (action === 'archive') {
        invalidateRequests('ARCHIVED');
      } else if (action === 'offered-times') {
        // Clearing the decline moves the row out of new requests, which the
        // updated appointment alone can no longer tell us
        invalidateRequests('REQUESTED');
      }
    },
    [],
  );

  const resendBookingLink = React.useCallback(
    async (appointment: Appt) => {
      // If the appointment is not an appointment, return
      if (
        !appointment ||
        !('status' in appointment) ||
        appointment.status !== 'ACCEPTED' ||
        !loadedUser
      )
        return;
      try {
        const payload = await getDeliveryMethods({
          customer: appointment.customer,
          title: 'Resend booking link',
        });
        if (!payload.mediums.length) return;

        await sendAppointmentReminder(appointment, payload);
        toast.success('Booking link re-sent');
      } catch (e) {
        toast.error('Error re-sending booking link');
      }
    },
    [getDeliveryMethods, loadedUser],
  );

  const openChat = React.useCallback(
    async (appointment: Appt) => {
      if (appointment && loadedUser) {
        try {
          openLoadingModal({
            header: 'Opening chat...',
            message: 'Please wait while we load the chat',
          });

          const chatResponse = await createAppointmentChat(
            appointment,
            loadedUser,
          );
          navigateToChat(navigate, chatResponse);
        } catch {
          toast.error('Error opening chat');
        } finally {
          closeLoadingModal();
        }
      }
    },
    [closeLoadingModal, loadedUser, navigate, openLoadingModal],
  );

  const getEmployees = React.useCallback(
    (omitEmployeeId?: string) =>
      loadedUser?.business?.employees.filter(
        e => e.isBookable && e.employeeId !== omitEmployeeId,
      ),
    [loadedUser?.business?.employees],
  );

  const getEmployeeSelectInput = React.useCallback(
    (omitEmployeeId?: string) => {
      const employees = getEmployees(omitEmployeeId);
      if (!employees?.length) return [];
      return [
        {
          inputType: 'select',
          name: 'employeeId',
          options: employees
            .filter(e => e.employeeId !== omitEmployeeId)
            .map(e => ({
              label: getEmployeeCalendarName(employees, e.employeeId),
              value: e.employeeId,
            })),
          placeholder: 'Select service provider',
        },
      ] as InputRow<{ employeeId: string }>;
    },
    [getEmployees],
  );

  const forwardToArtist = React.useCallback(
    async (appointment: Appt) => {
      if (!appointment || !('requestId' in appointment)) return;

      try {
        const employees = getEmployees();
        if (!employees?.length) return;

        const response = await asyncForm({
          form: {
            title: 'Forward request',
            submitButtonText: 'Forward Request',
            initialValues: {
              employeeId: '',
            },
            inputRows: [getEmployeeSelectInput()],
          },
          allowUntouched: false,
        });
        if (!response) return;

        const { employeeId } = response;
        const selectedEmployeeName = getEmployeeCalendarName(
          employees,
          employeeId,
        );

        const confirmed = await confirm({
          title: 'Forward request to an artist',
          message: `Forward this general request to ${selectedEmployeeName}? They'll be notified and added to the chat`,
        });
        if (!confirmed) return;

        openLoadingModal({
          header: 'Forwarding request',
          message: 'Please wait while we forward the request...',
        });

        const { appointmentId: newAppointmentId } =
          await forwardGeneralRequestToArtist(
            appointment.requestId,
            employeeId,
          );
        if (!newAppointmentId) return;

        await invalidateRequests('REQUESTED');
        closeAllModals();

        toast.success(`Request forwarded to ${selectedEmployeeName}`);
      } catch (e) {
        toast.error('Error forwarding request. Please try again.');
      } finally {
        closeLoadingModal();
      }
    },
    [
      getEmployees,
      asyncForm,
      getEmployeeSelectInput,
      confirm,
      openLoadingModal,
      closeAllModals,
      closeLoadingModal,
    ],
  );

  const changeArtist = React.useCallback(
    async (appointment: Appt) => {
      if (
        !appointment ||
        !('status' in appointment) ||
        !CHANGE_ARTIST_STATUS.includes(appointment.status)
      )
        return;

      const employees = getEmployees();
      const availableEmployees = getEmployees(appointment.employeeId);
      if (!availableEmployees?.length) return;

      try {
        const response = await asyncForm({
          form: {
            title: 'Change artist',
            submitButtonText: 'Change Artist',
            initialValues: {
              employeeId: '',
            },
            inputRows: [getEmployeeSelectInput(appointment.employeeId)],
          },
        });
        if (!response) return;

        const { employeeId } = response;
        if (!employeeId) return;

        const oldEmployeeName = appointment.employeeId
          ? getEmployeeCalendarName(employees, appointment.employeeId)
          : 'Unassigned';
        const newEmployeeName = getEmployeeCalendarName(employees, employeeId);

        const confirmed = await confirm({
          title: 'Change artist',
          message: `Change artist from ${oldEmployeeName} to ${newEmployeeName}? We'll move the request and update the chat`,
        });
        if (!confirmed) return;

        openLoadingModal({
          header: 'Changing artist',
          message: 'Please wait while we change the artist...',
        });

        const { appointmentId: updatedAppointmentId } =
          await changeRequestArtist(appointment.appointmentId, employeeId);
        if (!updatedAppointmentId) return;

        const statusToInvalidate =
          !('status' in appointment) || appointment.status === 'REQUESTED'
            ? 'REQUESTED'
            : 'ACCEPTED';
        await invalidateRequests(statusToInvalidate);
        closeAllModals();

        toast.success(`Request moved to ${newEmployeeName}`);
      } catch (e) {
        toast.error('Error changing artist. Please try again.');
      } finally {
        closeLoadingModal();
      }
    },
    [
      getEmployees,
      asyncForm,
      getEmployeeSelectInput,
      confirm,
      openLoadingModal,
      closeAllModals,
      closeLoadingModal,
    ],
  );

  const statusTabName: Record<string, string> = React.useMemo(
    () => ({
      REQUESTED: TAB_NAMES.NEW_REQUESTS,
      ACCEPTED: TAB_NAMES.ACCEPTED_REQUESTS,
      REJECTED: TAB_NAMES.REJECTED_REQUESTS,
      SCHEDULED: TAB_NAMES.CALENDAR,
      CONFIRMED: TAB_NAMES.CALENDAR,
    }),
    [],
  );

  const reactivateRequest = React.useCallback(
    async (appointment: Appt) => {
      if (
        !appointment ||
        !('status' in appointment) ||
        appointment.status !== 'CANCELED'
      )
        return;

      const targetTab =
        statusTabName[appointment.previousStatus ?? ''] ?? 'its previous';

      const confirmed = await confirm({
        title: 'Reactivate request',
        message: `This request will be reactivated and moved back to the ${targetTab} tab. Are you sure you want to continue?`,
      });
      if (!confirmed) return;

      try {
        openLoadingModal({
          header: 'Reactivating request',
          message: 'Please wait while we reactivate the request...',
        });

        await uncancelAppointment(appointment.appointmentId);
        await invalidateRequests('CANCELED');
        if (appointment.previousStatus) {
          await invalidateRequests(appointment.previousStatus);
        }
        closeAllModals();
        toast.success('Request reactivated');
      } catch (e) {
        toast.error('Error reactivating request. Please try again.');
      } finally {
        closeLoadingModal();
      }
    },
    [
      confirm,
      openLoadingModal,
      closeAllModals,
      closeLoadingModal,
      statusTabName,
    ],
  );

  const getReactivateAction = React.useCallback(
    (appointment: Appt) =>
      createFooterAction('Reactivate request', () =>
        reactivateRequest(appointment),
      ),
    [reactivateRequest],
  );

  const navigateToCustomer = React.useCallback(
    (customerId: string) => {
      if (!canManageRequest) {
        openSubscriptionModal();
        return;
      }
      navigateToCustomerProfile(customerId);
    },
    [canManageRequest, navigateToCustomerProfile, openSubscriptionModal],
  );

  const unarchive = React.useCallback(
    async (appointment: Appointment | GeneralRequest | undefined) => {
      if (loadedUser && appointment) {
        try {
          const { data } = await unarchiveAppointment(appointment);
          await invalidateRequests('REQUESTED');
          await invalidateRequests('ARCHIVED');

          let updatedAppointment: Appt;
          if ('requestId' in appointment) {
            const { deletedAt } = data.result as UnarchivedGeneralRequestFields;
            updatedAppointment = {
              ...appointment,
              deletedAt: deletedAt ?? undefined,
            };
          } else {
            const { status, archivedAt } =
              data.result as UnarchivedAppointmentFields;
            updatedAppointment = {
              ...appointment,
              status,
              archivedAt: archivedAt ?? undefined,
            };
          }

          setSelectedAppointment(updatedAppointment);
          setModalData(
            createModalData({
              appointment: updatedAppointment,
              sizeOptions,
              navigateToCustomer,
              allBusinesses: loadedUser?.allBusinesses,
              employeesToBusiness: loadedUser?.business?.employees,
              loadedUser,
            }),
          );
          toast.success('Request unarchived to New Requests');
        } catch {
          toast.error('Could not unarchive request');
        }
      }
    },
    [loadedUser, navigateToCustomer, sizeOptions],
  );

  const getUnarchiveAction = React.useCallback(
    (appointment: Appt) =>
      createFooterAction('Unarchive', () => unarchive(appointment)),
    [unarchive],
  );

  const undoRequestRejection = React.useCallback(
    async (appointment: Appt) => {
      if (!('appointmentId' in appointment) || !loadedUser) return;
      if (!canManageRequest) {
        openSubscriptionModal();
        return;
      }
      try {
        await undoRejection(appointment.appointmentId);
        await invalidateRequests('REJECTED');
        await invalidateRequests('REQUESTED');
        await invalidateUnreadRequests();

        const updatedAppointment: Appt = {
          ...appointment,
          status: 'REQUESTED',
          rejectedOn: undefined,
          rejectionMessage: undefined,
        };
        setSelectedAppointment(updatedAppointment);
        if (modalOpen) {
          setModalData(
            createModalData({
              appointment: updatedAppointment,
              sizeOptions,
              navigateToCustomer,
              allBusinesses: loadedUser?.allBusinesses,
              employeesToBusiness: loadedUser?.business?.employees,
              loadedUser,
            }),
          );
        }
        toast.success('Rejection undone');
      } catch {
        toast.error('Failed to undo rejection');
      }
    },
    [
      canManageRequest,
      loadedUser,
      modalOpen,
      navigateToCustomer,
      openSubscriptionModal,
      sizeOptions,
    ],
  );

  const getUndoRejectionAction = React.useCallback(
    (appointment: Appt) =>
      createFooterAction('Undo rejection', () =>
        undoRequestRejection(appointment),
      ),
    [undoRequestRejection],
  );

  const onEditRequestSuccess = React.useCallback(
    async (
      updated: Partial<Appointment & GeneralRequest>,
      action?: Parameters<typeof smartRefresh>[1],
      // Table-row menus fire from a stale closure, so they pass their own row
      target?: Appt,
    ) => {
      const edited = target ?? selectedAppointment;
      if (!edited || !loadedUser) return;
      const updatedAppointment = { ...edited, ...updated } as Appt;
      setSelectedAppointment(updatedAppointment);
      if (modalOpen) {
        setModalData(
          createModalData({
            appointment: updatedAppointment,
            sizeOptions,
            navigateToCustomer,
            allBusinesses: loadedUser?.allBusinesses,
            employeesToBusiness: loadedUser?.business?.employees,
            loadedUser,
          }),
        );
      }
      await smartRefresh(updatedAppointment, action);
    },
    [
      loadedUser,
      modalOpen,
      navigateToCustomer,
      selectedAppointment,
      sizeOptions,
      smartRefresh,
    ],
  );

  const getForwardToArtistAction = React.useCallback(
    (appointment: Appt) =>
      createFooterAction('Forward to artist', () =>
        forwardToArtist(appointment),
      ),
    [forwardToArtist],
  );
  const getChangeArtistAction = React.useCallback(
    (appointment: Appt) =>
      createFooterAction('Change artist', () => changeArtist(appointment)),
    [changeArtist],
  );
  const archiveAction = React.useMemo(
    () => createFooterAction('Archive', openArchive),
    [openArchive],
  );
  const scheduleAppointmentAction = React.useMemo(
    () =>
      createFooterAction('Schedule appointment', () =>
        openAppointmentForm('NEW_APPOINTMENT'),
      ),
    [openAppointmentForm],
  );
  const scheduleConsultationAction = React.useMemo(
    () =>
      createFooterAction('Schedule consultation', () =>
        openAppointmentForm('CONSULTATION'),
      ),
    [openAppointmentForm],
  );
  const getOpenChatAction = React.useCallback(
    (appointment: Appt) =>
      createFooterAction('Open chat', () => openChat(appointment)),
    [openChat],
  );
  const getResendBookingLinkAction = React.useCallback(
    (appointment: Appt) =>
      createFooterAction(
        (appointment as Appointment).offeredSlots?.length
          ? 'Resend offered times'
          : 'Resend booking link',
        () => resendBookingLink(appointment),
      ),
    [resendBookingLink],
  );
  const getEditOfferedTimesAction = React.useCallback(
    (appointment: Appointment) =>
      createFooterAction('Edit offered times', () => {
        setOfferedTimesAppointment(appointment);
      }),
    [],
  );
  const getRemoveOfferedTimesAction = React.useCallback(
    (appointment: Appointment) =>
      createFooterAction('Remove offered times', async () => {
        const confirmed = await confirm({
          dismissible: true,
          title: 'Remove offered times?',
          message: `${
            appointment.customer?.firstName || 'The client'
          } will be able to book any available time instead.`,
        });
        if (!confirmed) return;

        try {
          // An empty set clears the offers and puts the link back on full
          // availability. The server drops the decline with them, so the
          // sidebar has to lose its warning in the same breath.
          await updateOfferedSlots(appointment, []);
          toast.success('Offered times removed');
          await onEditRequestSuccess(
            { offeredSlots: [], offeredTimesDeclinedAt: null },
            'offered-times',
            appointment,
          );
        } catch {
          toast.error("Couldn't remove the offered times. Please try again.");
        }
      }),
    [confirm, onEditRequestSuccess],
  );
  const getMarkDepositPaidAction = React.useCallback(
    (appointment: Appointment) =>
      createFooterAction('Checkout deposit', () =>
        openDepositCheckout(
          appointment.appointmentId,
          () => smartRefresh(appointment),
          { allowTerminal: false },
        ),
      ),
    [openDepositCheckout, smartRefresh],
  );
  const acceptAction = React.useMemo(
    () => ({
      ...createFooterAction('Accept', acceptRequest),
      coachMark: <CoachMark hintKey="ACCEPT_REQUEST_FLOW" />,
    }),
    [acceptRequest],
  );
  const rejectAction = React.useMemo(
    () => createFooterAction('Reject', rejectRequest),
    [rejectRequest],
  );

  const canEditRequest = React.useCallback(
    (appointment: Appt) =>
      appointment.businessId === loadedUser?.business?.id &&
      ('requestId' in appointment
        ? !appointment.deletedAt &&
          !!loadedUser?.generalRequestOwner &&
          (isUserAdminOrManager(loadedUser) ||
            loadedUser?.business?.type !== 'STUDIO')
        : EDIT_REQUEST_STATUS.includes(appointment.status) &&
          (appointment.employeeId === loadedUser?.userId ||
            isUserAdminOrManager(loadedUser))),
    [loadedUser],
  );

  const editRequestAction = React.useMemo(
    () => createFooterAction('Edit request', openEditRequest),
    [openEditRequest],
  );

  const canChangeArtist = React.useCallback(
    (appointment: Appt) =>
      'status' in appointment &&
      appointment.businessId === loadedUser?.business?.id &&
      CHANGE_ARTIST_STATUS.includes(appointment.status) &&
      (appointment.employeeId === loadedUser?.userId ||
        isUserAdminOrManager(loadedUser)) &&
      getEmployees(appointment.employeeId)?.length,
    [getEmployees, loadedUser],
  );

  const actions = React.useMemo(() => {
    // Offered times sit directly under Edit request — they are the same kind of
    // "change what the client sees" action. Change them, send them again, or
    // drop them for full availability; nothing left to send once they have all
    // elapsed.
    const appointmentWithOfferedTimes =
      getApptWithOfferedTimes(selectedAppointment);
    const offeredTimesActions = appointmentWithOfferedTimes
      ? [
          getEditOfferedTimesAction(appointmentWithOfferedTimes),
          ...(splitOfferedSlots(appointmentWithOfferedTimes.offeredSlots).live
            .length
            ? [getResendBookingLinkAction(appointmentWithOfferedTimes)]
            : []),
          getRemoveOfferedTimesAction(appointmentWithOfferedTimes),
        ]
      : [];

    const moreRequested = [
      ...(!!selectedAppointment && canEditRequest(selectedAppointment)
        ? [editRequestAction]
        : []),
      ...offeredTimesActions,
      archiveAction,
    ];
    if (!!selectedAppointment && 'status' in selectedAppointment) {
      if (selectedAppointment.status === 'ACCEPTED') {
        // Accepted requests keep the classic schedule flows
        moreRequested.push(
          scheduleAppointmentAction,
          scheduleConsultationAction,
        );
        if (!appointmentWithOfferedTimes) {
          moreRequested.push(getResendBookingLinkAction(selectedAppointment));
        }
        if (
          (selectedAppointment.depositPrice ?? 0) > 0 &&
          !isAppointmentDepositPaid(selectedAppointment)
        ) {
          moreRequested.push(getMarkDepositPaidAction(selectedAppointment));
        }
        moreRequested.push(rejectAction);
      }

      if (canChangeArtist(selectedAppointment)) {
        moreRequested.push(getChangeArtistAction(selectedAppointment));
      }
    }

    if (!!selectedAppointment && 'requestId' in selectedAppointment) {
      moreRequested.push(getForwardToArtistAction(selectedAppointment));
    }
    return {
      footer: [
        acceptAction,
        rejectAction,
        getOpenChatAction(selectedAppointment!),
      ],
      acceptedFooter: [getOpenChatAction(selectedAppointment!)],
      canceledFooter: [getOpenChatAction(selectedAppointment!)],
      more: [archiveAction],
      moreRequested,
      moreArchived: [
        getUnarchiveAction(selectedAppointment!),
        scheduleAppointmentAction,
        scheduleConsultationAction,
      ],
      moreRejected: [
        getUndoRejectionAction(selectedAppointment!),
        archiveAction,
      ],
      moreCanceled: [
        getReactivateAction(selectedAppointment!),
        getOpenChatAction(selectedAppointment!),
        archiveAction,
      ],
    };
  }, [
    acceptAction,
    archiveAction,
    canChangeArtist,
    canEditRequest,
    editRequestAction,
    getEditOfferedTimesAction,
    getRemoveOfferedTimesAction,
    getChangeArtistAction,
    getForwardToArtistAction,
    getMarkDepositPaidAction,
    getOpenChatAction,
    getReactivateAction,
    getResendBookingLinkAction,
    getUnarchiveAction,
    getUndoRejectionAction,
    rejectAction,
    scheduleAppointmentAction,
    scheduleConsultationAction,
    selectedAppointment,
  ]);

  const archive = React.useCallback(
    async (appointment: Appointment | GeneralRequest | undefined) => {
      if (loadedUser && appointment) {
        try {
          await archiveAppointment(appointment);
          closeAllModals();

          await smartRefresh(appointment, 'archive');

          toast.success('Appointment archived');
        } catch {
          toast.error('Could not archive appointment');
        }
      }
    },
    [closeAllModals, loadedUser, smartRefresh],
  );

  const reject = React.useCallback(
    async (appointment: Appointment | GeneralRequest, values: RejectValues) => {
      if ((appointment as Appointment).appointmentId) {
        try {
          await rejectAppointment(appointment as Appointment, values);
          toast.success('Appointment Rejected');
          setFormError('');
          await smartRefresh(appointment, 'reject');
        } catch {
          setFormError(
            'Error updating appointment details. Please refresh the page and try again',
          );
          toast.error('Error rejecting appointment');
        } finally {
          closeAllModals();
        }
      } else {
        try {
          await rejectGeneralRequest(
            appointment as GeneralRequest,
            values,
            loadedUser?.userId || '',
          );
          toast.success('Appointment Rejected');
          setFormError('');
          await smartRefresh(appointment, 'reject');
        } catch {
          setFormError(
            'Error updating appointment details. Please refresh the page and try again',
          );
          toast.error('Error rejecting appointment');
        } finally {
          closeAllModals();
        }
      }
    },
    [closeAllModals, loadedUser?.userId, smartRefresh],
  );

  // Create tabs and tab content with each table
  const getTableActions = React.useCallback(
    (appointment: Appt) => {
      const openRequestAction = () =>
        openRequest(
          createModalData({
            allBusinesses: loadedUser?.allBusinesses,
            employeesToBusiness: loadedUser?.business?.employees,
            appointment,
            sizeOptions,
            navigateToCustomer,
            loadedUser,
          }),
          appointment,
        );

      const viewRequestAction = createFooterAction(
        'View request',
        openRequestAction,
      );

      const requestedActions = [
        viewRequestAction,
        canEditRequest(appointment) && editRequestAction,
        acceptAction,
        canChangeArtist(appointment) && getChangeArtistAction(appointment),
        'requestId' in appointment && getForwardToArtistAction(appointment),
        getOpenChatAction(appointment),
        rejectAction,
        archiveAction,
      ].filter(Boolean) as FooterAction[];

      // With offers in play they replace the plain resend link, as in the sidebar
      const appointmentWithOfferedTimes = getApptWithOfferedTimes(appointment);

      const acceptedActions = [
        viewRequestAction,
        canEditRequest(appointment) && editRequestAction,
        ...(appointmentWithOfferedTimes
          ? [
              getEditOfferedTimesAction(appointmentWithOfferedTimes),
              ...(splitOfferedSlots(appointmentWithOfferedTimes.offeredSlots)
                .live.length
                ? [getResendBookingLinkAction(appointmentWithOfferedTimes)]
                : []),
              getRemoveOfferedTimesAction(appointmentWithOfferedTimes),
            ]
          : [getResendBookingLinkAction(appointment)]),
        'depositPrice' in appointment &&
          (appointment.depositPrice ?? 0) > 0 &&
          !isAppointmentDepositPaid(appointment) &&
          getMarkDepositPaidAction(appointment),
        canChangeArtist(appointment) && getChangeArtistAction(appointment),
        scheduleAppointmentAction,
        scheduleConsultationAction,
        getOpenChatAction(appointment),
        rejectAction,
        archiveAction,
      ].filter(Boolean) as FooterAction[];

      const rejectedActions = [
        viewRequestAction,
        getUndoRejectionAction(appointment),
        getOpenChatAction(appointment),
        archiveAction,
      ].filter(Boolean) as FooterAction[];

      const canceledActions = [
        viewRequestAction,
        getReactivateAction(appointment),
        getOpenChatAction(appointment),
        archiveAction,
      ].filter(Boolean) as FooterAction[];

      const isArchived =
        appointment.deletedAt ||
        ('archivedAt' in appointment && appointment.archivedAt);
      const tableActions = [
        ...(isArchived
          ? []
          : 'requestId' in appointment || appointment.status === 'REQUESTED'
          ? requestedActions
          : appointment.status === 'ACCEPTED'
          ? acceptedActions
          : appointment.status === 'REJECTED'
          ? rejectedActions
          : appointment.status === 'CANCELED'
          ? canceledActions
          : []),
      ];

      return tableActions;
    },
    [
      acceptAction,
      archiveAction,
      canChangeArtist,
      canEditRequest,
      editRequestAction,
      getChangeArtistAction,
      getEditOfferedTimesAction,
      getForwardToArtistAction,
      getMarkDepositPaidAction,
      getOpenChatAction,
      getReactivateAction,
      getRemoveOfferedTimesAction,
      getResendBookingLinkAction,
      getUndoRejectionAction,
      loadedUser,
      navigateToCustomer,
      openRequest,
      rejectAction,
      scheduleAppointmentAction,
      scheduleConsultationAction,
      sizeOptions,
    ],
  );

  useGetAppointment(appointmentId!, {
    enabled:
      !!sizeOptions &&
      !!loadedUser?.allBusinesses &&
      !!loadedUser?.business?.employees,
    onSuccess: appt => {
      const newModalData = createModalData({
        appointment: appt,
        sizeOptions,
        navigateToCustomer,
        allBusinesses: loadedUser?.allBusinesses,
        employeesToBusiness: loadedUser?.business?.employees,
        loadedUser,
      });

      openRequest(newModalData, appt);
    },
    staleTime: 0,
    gcTime: 0,
  });

  const renderSideBarModal = React.useCallback(() => {
    if (!modalData || !selectedAppointment) return null;
    const appt = selectedAppointment as Appointment;
    const generalRequest = selectedAppointment as GeneralRequest;
    const isGeneralRequest = 'requestId' in generalRequest;
    const isArchived =
      appt.status === 'ARCHIVED' || !!generalRequest?.deletedAt;

    const isRequested =
      (isGeneralRequest && !generalRequest.deletedAt) ||
      appt.status === 'REQUESTED';
    const isAccepted = appt.status === 'ACCEPTED';
    const isRejected = appt.status === 'REJECTED';
    const isCanceled = appt.status === 'CANCELED';

    return (
      <SideBarModal
        modalOpen={modalOpen}
        setModalOpen={onCloseRequest}
        name={modalData.name}
        sections={modalData.sections}
        footerActions={
          isRequested
            ? actions.footer
            : isArchived || isAccepted || isRejected
            ? actions.acceptedFooter
            : isCanceled
            ? actions.canceledFooter
            : []
        }
        moreActions={
          isRequested || isAccepted
            ? actions.moreRequested
            : isArchived
            ? actions.moreArchived
            : isRejected
            ? actions.moreRejected
            : isCanceled
            ? actions.moreCanceled
            : actions.more
        }
      />
    );
  }, [
    actions.acceptedFooter,
    actions.canceledFooter,
    actions.footer,
    actions.more,
    actions.moreArchived,
    actions.moreCanceled,
    actions.moreRejected,
    actions.moreRequested,
    modalData,
    modalOpen,
    onCloseRequest,
    selectedAppointment,
  ]);

  const error = isError ? 'Error loading requests' : '';

  const formModalName =
    createAptType === 'NEW_APPOINTMENT'
      ? 'Schedule appointment'
      : 'Schedule consultation';

  if (error !== '') {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage={error} />
      </Box>
    );
  }

  return (
    <>
      <PageLayout title="Booking Requests">
        {artist.isVisible && (
          <Stack direction="row" alignItems="center" gap={1}>
            <MultiSelectFilter
              name={artist.name}
              options={artist.options}
              state={artist.state}
              setState={artist.setState}
              emptyLabel={artist.emptyLabel}
              allLabel={artist.allLabel}
              sx={{ m: 0 }}
            />
          </Stack>
        )}

        <TabBar
          tabs={[
            {
              id: 'REQUESTED',
              label: TAB_NAMES.NEW_REQUESTS,
            },
            {
              id: 'ACCEPTED',
              label: TAB_NAMES.ACCEPTED_REQUESTS,
            },
            {
              id: 'REJECTED',
              label: TAB_NAMES.REJECTED_REQUESTS,
            },
            {
              id: 'CANCELED',
              label: TAB_NAMES.CANCELED_BY_CUSTOMER,
            },
            {
              id: 'ARCHIVED',
              label: 'Archived Requests',
            },
          ]}
          currentTab={getTabValue(currentTab)}
          changeTab={tab => {
            setCurrentTab(TAB_ORDER[tab] ?? 'REQUESTED');
          }}
          sharedComponent={
            isLgScreen ? (
              <RequestsTable
                requests={appointments}
                isLoading={isLoading}
                sort={sort}
                tab={currentTab}
                count={isLoading ? 0 : totalCount}
                getActions={getTableActions}
                beforeAction={setSelectedAppointment}
                onEndReached={onEndReached}
                onOpenRequest={appt =>
                  openRequest(
                    createModalData({
                      allBusinesses: loadedUser?.allBusinesses,
                      employeesToBusiness: loadedUser?.business?.employees,
                      appointment: appt,
                      sizeOptions,
                      navigateToCustomer,
                      loadedUser,
                    }),
                    appt,
                  )
                }
              />
            ) : (
              <RequestsList
                isLoading={isLoading}
                requests={appointments}
                tab={currentTab}
                onOpenRequest={appt =>
                  openRequest(
                    createModalData({
                      allBusinesses: loadedUser?.allBusinesses,
                      employeesToBusiness: loadedUser?.business?.employees,
                      appointment: appt,
                      sizeOptions,
                      navigateToCustomer,
                      loadedUser,
                    }),
                    appt,
                  )
                }
                onEndReached={onEndReached}
              />
            )
          }
        />

        {renderSideBarModal()}
      </PageLayout>
      {offeredTimesAppointment && (
        <EditOfferedTimesDialog
          open
          appointment={offeredTimesAppointment}
          onClose={() => setOfferedTimesAppointment(null)}
          // The server clears the decline with the same write, so the open
          // sidebar has to lose its warning and old times in the same breath
          onSaved={starts =>
            onEditRequestSuccess(
              {
                offeredSlots: starts.map(startDateTime => ({
                  id: startDateTime,
                  startDateTime,
                })),
                offeredTimesDeclinedAt: null,
              },
              'offered-times',
              offeredTimesAppointment,
            )
          }
        />
      )}
      <FullPageModal
        modalOpen={acceptModalOpen}
        setModalOpen={setAcceptModalOpen}
        name="Accept Request"
        confirmClose={async () => {
          if (!acceptDirtyCheckRef.current?.()) return true;
          return confirm({
            dismissible: true,
            title: 'Discard changes?',
            message: "Your changes won't be saved.",
            positiveText: 'Discard',
            negativeText: 'Keep editing',
          });
        }}>
        <AcceptRequestFlow
          request={selectedAppointment}
          closeModals={closeAllModals}
          refreshData={smartRefresh}
          onScheduled={apptId => navigate(`${ROUTES.calendar}/${apptId}`)}
          registerDirtyCheck={isDirty => {
            acceptDirtyCheckRef.current = isDirty;
          }}
        />
      </FullPageModal>
      <FullPageModal
        modalOpen={editRequestModalOpen}
        setModalOpen={setEditRequestModalOpen}
        name="Edit Request">
        {selectedAppointment && (
          <EditRequestForm
            request={selectedAppointment}
            onClose={() => setEditRequestModalOpen(false)}
            onSuccess={onEditRequestSuccess}
          />
        )}
      </FullPageModal>
      <FullPageModal
        modalOpen={rejectModalOpen}
        setModalOpen={setRejectModalOpen}
        name="Reject Request">
        <ReuseableForm
          initFormValues={rejectFormValues}
          validationSchema={rejectValidationSchema}
          inputGroups={rejectInputGroups}
          selectedAppointment={selectedAppointment}
          submitAction={reject}
          formError={formError}
        />
      </FullPageModal>

      <FullPageModal
        modalOpen={appointmentFormModalOpen}
        setModalOpen={setAppointmentFormModalOpen}
        handleClose={closeAppointmentForm}
        shouldScrollTop={shouldScrollToTop}
        setShouldScrollToTop={setShouldScrollToTop}
        name={formModalName}>
        <AppointmentForm
          selectedRequest={selectedAppointment}
          onClose={() => setAppointmentFormModalOpen(false)}
          onCreateUpdateSuccess={async params => {
            if (selectedAppointment) await smartRefresh(selectedAppointment);
            await invalidateAppointments();

            const currentAptId =
              selectedAppointment && 'appointmentId' in selectedAppointment
                ? selectedAppointment.appointmentId
                : '';
            const { newAptId } = params;

            const apptId = currentAptId || newAptId;
            if (apptId) {
              navigate(`${ROUTES.calendar}/${apptId}`);
            }
          }}
          setShouldScrollToTop={setShouldScrollToTop}
          appointmentType={createAptType}
        />
      </FullPageModal>

      <ConfirmationModal
        open={archiveModalOpen}
        handleClose={() => setArchiveModalOpen(false)}
        title="Archive Request"
        message={
          selectedAppointment &&
          (selectedAppointment as Appointment).appointmentId &&
          (selectedAppointment as Appointment).status === 'ACCEPTED'
            ? 'This action cannot be undone. Are you sure you want to archive this request? Archiving will still allow customers to schedule this appointment. If you no longer want them to, reject the request instead.'
            : 'This action cannot be undone. Are you sure you want to archive this request? You will no longer be able to view the details of this request once you archive.'
        }
        negativeAction={() => setArchiveModalOpen(false)}
        positiveAction={() => archive(selectedAppointment)}
        negativeText=""
        positiveText="Archive"
      />
    </>
  );
}
